import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Alert, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Chip, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import PublicIcon from '@mui/icons-material/Public';
import NoEncryptionGmailerrorredIcon from '@mui/icons-material/NoEncryptionGmailerrorred';
import ShareIcon from '@mui/icons-material/Share';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useNavigate } from 'react-router-dom';
import {
  getMaciePostureSummary, triggerMacieRefresh, getMacieRefreshJob, getMacieActiveRefreshJob,
} from '../../../services/macieApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalEmptyState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants,
} from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  statusChipSx, statTileSx, statTileFooterSx, tileEntranceStyle
} from '../portalStyles';
import { severityColor } from './severity';

const MACIE_COLOR = '#0d9488';
const MACIE_COLOR_DARK = '#0B7C72';
const SEVERITY_ORDER = ['HIGH', 'MEDIUM', 'LOW'];

const severityMeta = {
  HIGH:   { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  MEDIUM: { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  LOW:    { icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} /> },
};

// Macie-accented header buttons (the theme default is the CSPM blue).
const macieContainedSx = {
  backgroundColor: MACIE_COLOR,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { backgroundColor: MACIE_COLOR_DARK },
  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
};

const macieOutlinedSx = {
  color: MACIE_COLOR,
  borderColor: `${MACIE_COLOR}40`,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': { borderColor: MACIE_COLOR, backgroundColor: `${MACIE_COLOR}0A` },
};

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

// StatTile is the one tile shape both stat rows use, so the severity row and the
// bucket-posture row are exactly the same height and read as one system.
function StatTile({ icon, value, color, label, caption, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        ...statTileSx,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '10px',
            backgroundColor: `${color}12`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 700,
            fontSize: '2.5rem',
            color,
            lineHeight: 1,
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box sx={statTileFooterSx}>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontWeight: 600,
            fontSize: '1rem',
            color: CK.navy,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '0.8rem',
            color: CK.muted,
            fontWeight: 500,
          }}
        >
          {caption}
        </Typography>
      </Box>
    </Paper>
  );
}

export default function MacieDashboard({ config }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [findingsJob, setFindingsJob] = useState(null);
  const [bucketsJob, setBucketsJob] = useState(null);
  const findingsPollRef = useRef(null);
  const bucketsPollRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMaciePostureSummary();
      setSummary(res?.data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Re-attach to a findings refresh already running server-side (started before a
  // navigation away or a full page reload). A refresh started this session wins.
  useEffect(() => {
    let ignore = false;
    getMacieActiveRefreshJob('findings')
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setFindingsJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient — the user can still refresh manually */ });
    return () => { ignore = true; };
  }, []);

  // Re-attach to a buckets refresh already running server-side.
  useEffect(() => {
    let ignore = false;
    getMacieActiveRefreshJob('buckets')
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setBucketsJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient */ });
    return () => { ignore = true; };
  }, []);

  // Poll the findings job while it runs; reload the summary when it finishes.
  useEffect(() => {
    if (!findingsJob || findingsJob.status !== 'running') return undefined;
    findingsPollRef.current = setInterval(async () => {
      try {
        const res = await getMacieRefreshJob(findingsJob.id);
        const next = res?.data;
        setFindingsJob(next);
        if (next && next.status !== 'running') {
          clearInterval(findingsPollRef.current);
          findingsPollRef.current = null;
          await loadSummary();
        }
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => {
      if (findingsPollRef.current) {
        clearInterval(findingsPollRef.current);
        findingsPollRef.current = null;
      }
    };
  }, [findingsJob, loadSummary]);

  // Poll the buckets job while it runs; reload the summary when it finishes.
  useEffect(() => {
    if (!bucketsJob || bucketsJob.status !== 'running') return undefined;
    bucketsPollRef.current = setInterval(async () => {
      try {
        const res = await getMacieRefreshJob(bucketsJob.id);
        const next = res?.data;
        setBucketsJob(next);
        if (next && next.status !== 'running') {
          clearInterval(bucketsPollRef.current);
          bucketsPollRef.current = null;
          await loadSummary();
        }
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => {
      if (bucketsPollRef.current) {
        clearInterval(bucketsPollRef.current);
        bucketsPollRef.current = null;
      }
    };
  }, [bucketsJob, loadSummary]);

  const onRefreshFindings = async () => {
    try {
      const res = await triggerMacieRefresh('findings');
      setFindingsJob(res?.data);
    } catch (e) {
      setError(e.message);
    }
  };

  const onRefreshBuckets = async () => {
    try {
      const res = await triggerMacieRefresh('buckets');
      setBucketsJob(res?.data);
    } catch (e) {
      setError(e.message);
    }
  };

  // The header stays mounted through loading and error so the page never jumps
  // when the posture summary lands.
  const baseHeader = (
    <PortalPageHeader
      icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
      title="Macie Dashboard"
      color={MACIE_COLOR}
      subtitle={(
        <>
          Admin account <b>{config.adminAccountId}</b> • Home region <b>{config.homeRegion}</b>
        </>
      )}
    />
  );

  if (loading && !summary) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{baseHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading Macie posture…" color={MACIE_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{baseHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={loadSummary} />
        </motion.div>
      </motion.div>
    );
  }
  if (!summary) return null;

  const totalFindings = SEVERITY_ORDER.reduce(
    (n, s) => n + (summary.severityCounts?.[s] ?? 0),
    0
  );
  const bucketStats = summary.bucketStats || {};
  const findingsRefreshing = findingsJob?.status === 'running';
  const bucketsRefreshing = bucketsJob?.status === 'running';
  const lastFindings = summary.lastFindingsRefreshAt ? relativeTime(summary.lastFindingsRefreshAt) : 'never';
  const lastBuckets = summary.lastBucketsRefreshAt ? relativeTime(summary.lastBucketsRefreshAt) : 'never';

  const postureCards = [
    { key: 'total', label: 'S3 buckets', value: bucketStats.total ?? 0, color: CK.blue, icon: <StorageIcon sx={{ fontSize: 28 }} />, to: '/securityhub/macie/buckets' },
    { key: 'public', label: 'Publicly accessible', value: bucketStats.public ?? 0, color: CK.red, icon: <PublicIcon sx={{ fontSize: 28 }} />, to: '/securityhub/macie/buckets?public=PUBLIC' },
    { key: 'unencrypted', label: 'Unencrypted', value: bucketStats.unencrypted ?? 0, color: CK.amber, icon: <NoEncryptionGmailerrorredIcon sx={{ fontSize: 28 }} />, to: '/securityhub/macie/buckets?encryption=NONE' },
    { key: 'sharedExternal', label: 'Shared externally', value: bucketStats.sharedExternal ?? 0, color: CK.purple, icon: <ShareIcon sx={{ fontSize: 28 }} />, to: '/securityhub/macie/buckets?shared=EXTERNAL' },
  ];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* === Header === */}
      <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<FindInPageOutlinedIcon sx={{ fontSize: 24 }} />}
        title="Macie Dashboard"
        color={MACIE_COLOR}
        subtitle={(
          <>
            Admin account <b>{config.adminAccountId}</b> • Home region <b>{config.homeRegion}</b>
            {' • '}Findings: <b>{lastFindings}</b> • Buckets: <b>{lastBuckets}</b>
            {summary.autoDiscoveryEnabled && (
              <>
                {' '}
                <Chip size="small" label="Automated discovery on" sx={{ ...statusChipSx('info'), ml: 0.5 }} />
              </>
            )}
          </>
        )}
        action={(
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Tooltip title={findingsRefreshing ? 'Findings refresh already in progress' : 'Fetch the latest findings from AWS'}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={findingsRefreshing}
                  onClick={onRefreshFindings}
                  sx={macieContainedSx}
                >
                  {findingsRefreshing ? 'Refreshing…' : 'Refresh findings'}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={bucketsRefreshing ? 'Bucket refresh already in progress' : 'Re-scan S3 bucket posture from AWS'}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={bucketsRefreshing}
                  onClick={onRefreshBuckets}
                  sx={macieOutlinedSx}
                >
                  {bucketsRefreshing ? 'Refreshing…' : 'Refresh buckets'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        )}
      />
      </motion.div>

      {findingsRefreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={findingsJob?.progress} label={findingsJob?.progressLabel || 'Refreshing findings…'} sx={{ mb: 2 }} />
        </motion.div>
      )}
      {bucketsRefreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={bucketsJob?.progress} label={bucketsJob?.progressLabel || 'Refreshing bucket posture…'} sx={{ mb: 2 }} />
        </motion.div>
      )}

      {config.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button
                size="small"
                onClick={() => navigate('/securityhub/macie/enrollment')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none' }}
              >
                Review
              </Button>
            }
          >
            Macie setup partially failed. {config.errorMessage}
          </Alert>
        </motion.div>
      )}

      {/* === Severity stat cards ===
          No motion wrapper: the tiles animate themselves in pure CSS, and a
          motion component here can write an inline opacity onto them once its
          own animation settles. */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {SEVERITY_ORDER.map((sev, i) => {
          const n = summary.severityCounts?.[sev] ?? 0;
          const m = severityMeta[sev];
          const color = severityColor(sev);
          return (
            <Grid size={{ xs: 12, sm: 4 }} key={sev}>
              <div style={tileEntranceStyle(i)}>
                <StatTile
                  icon={m.icon}
                  value={n}
                  color={color}
                  label={sev.charAt(0) + sev.slice(1).toLowerCase()}
                  caption={n === 1 ? 'active finding' : 'active findings'}
                  onClick={() => navigate(`/securityhub/macie/findings?severity=${sev}`)}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>

      <motion.div variants={sectionVariants}>
        <Typography
          sx={{
            fontFamily: FONT_BODY,
            fontSize: '0.75rem',
            color: CK.muted,
            display: 'block',
            mb: 2,
          }}
        >
          Total active findings across all severities: <b>{totalFindings}</b>
        </Typography>
      </motion.div>

      {/* === Bucket-posture stat row === */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {postureCards.map((c, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={c.key}>
            <div style={tileEntranceStyle(i)}>
              <StatTile
                icon={c.icon}
                value={c.value}
                color={c.color}
                label={c.label}
                caption={c.value === 1 ? 'bucket' : 'buckets'}
                onClick={() => navigate(c.to)}
              />
            </div>
          </Grid>
        ))}
      </Grid>

      {/* === Top finding types === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <PortalSectionHeader
          icon={<BarChartIcon sx={{ fontSize: 20 }} />}
          title="Top finding types"
          subtitle="Where Macie is raising the most findings"
          color={MACIE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${(summary.perType || []).length} types`}
              sx={statusChipSx('info')}
            />
          )}
        />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Type</TableCell>
                <TableCell align="right" sx={{ ...headCellSx, width: 120 }}>Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.perType || []).map((row, index) => (
                <TableRow
                  key={row.type}
                  hover
                  sx={{ ...rowSx(index), cursor: 'pointer' }}
                  onClick={() => navigate('/securityhub/macie/findings?type=' + encodeURIComponent(row.type))}
                >
                  <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{row.type}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>
                    <Chip label={row.count} size="small" sx={{ ...statusChipSx('neutral'), minWidth: 60 }} />
                  </TableCell>
                </TableRow>
              ))}
              {(!summary.perType || summary.perType.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<BarChartIcon sx={{ fontSize: 28 }} />}
                      title="No findings detected yet"
                      description="Run “Refresh findings” to pull the latest Macie results from AWS."
                      color={MACIE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>

      {/* === Per-account table === */}
      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={sectionCardSx}>
        <PortalSectionHeader
          icon={<AccountTreeIcon sx={{ fontSize: 20 }} />}
          title="Per-account findings"
          subtitle="Active Macie findings by enrolled account"
          color={MACIE_COLOR}
          action={(
            <Chip
              size="small"
              label={`${(summary.perAccount || []).length} accounts with findings`}
              sx={statusChipSx('info')}
            />
          )}
        />
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Account</TableCell>
                <TableCell align="right" sx={{ ...headCellSx, width: 180 }}>Active findings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.perAccount || []).map((row, index) => (
                <TableRow
                  key={row.accountId}
                  hover
                  sx={{ ...rowSx(index), cursor: 'pointer' }}
                  onClick={() => navigate('/securityhub/macie/findings?account=' + encodeURIComponent(row.accountId))}
                >
                  <TableCell sx={bodyCellSx}>{row.accountId}</TableCell>
                  <TableCell align="right" sx={bodyCellSx}>
                    <Chip
                      icon={<WarningAmberIcon />}
                      label={row.count}
                      size="small"
                      sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 80 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {(!summary.perAccount || summary.perAccount.length === 0) && (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                    <PortalEmptyState
                      icon={<AccountTreeIcon sx={{ fontSize: 28 }} />}
                      title="No accounts with active findings"
                      description="Enrolled accounts appear here as soon as Macie reports a finding for them."
                      color={MACIE_COLOR}
                      py={2}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </motion.div>
    </motion.div>
  );
}
